import { useState, useEffect, useContext } from 'react';
import { expValEquals } from '@confluence/feature-experiments';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { Provider, CollabEditProvider } from '@atlaskit/collab-provider';

import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';

import {
	EDIT_PAGE_LOAD_EXPERIENCE,
	COMPANY_HUB_EDITOR_LOAD_EXPERIENCE,
	VIEW_PAGE_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { getLogger } from '@confluence/logger';
import { COLLAB_EDIT_PROVIDER_MEASURES } from '@confluence/fabric-providers/entry-points/CollabEditProviderMeasures.ts';
import { CollabEditProviderMeasures } from '@confluence/action-measures/entry-points/CollabEditProviderMeasures.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

import { useNativeCollabState } from './useNativeCollabState.ts';
import {
	useWaitForPublishButtonToBeEnabled,
	WaitForPublishButtonComponentUnmountedError,
	WaitForPublishButtonPreviousCallNotCompletedError,
	WaitForPublishButtonTabInactiveError,
} from './useWaitForPublishButtonToBeEnabled.ts';

const logger = getLogger('native-collab');

export const useInit = (
	collabEditProvider: (CollabEditProvider & Provider) | null,
	contentId: string,
	isCompanyHub?: boolean,
) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [{ collabEditInitialized }, nativeCollabActions] = useNativeCollabState();

	const [title, setTitle] = useState<string | null>(null);

	const experienceTracker = useContext(ExperienceTrackerContext);

	const isLivePage = useIsCurrentPageLive();

	let currentEditorPageLoadExperienceName = EDIT_PAGE_LOAD_EXPERIENCE;
	if (isLivePage) {
		currentEditorPageLoadExperienceName = VIEW_PAGE_EXPERIENCE;
	}
	if (isCompanyHub) {
		currentEditorPageLoadExperienceName = COMPANY_HUB_EDITOR_LOAD_EXPERIENCE;
	}
	const waitForPublishButtonToBeEnabled = useWaitForPublishButtonToBeEnabled();

	useEffect(() => {
		if (!collabEditProvider) {
			return;
		}

		const handleInit = (data) => {
			logger.debug`Collab Provider initialised at ${performance.now() / 1000}`;
			const newTitle = data?.metadata?.title ?? '';
			setTitle(newTitle);
			nativeCollabActions.setCollabEditInitialized(true);
			// marking provider initialization as complete once FE receives "init" event
			CollabEditProviderMeasures.markMeasureEnd(
				COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_DISABLED,
			);
			CollabEditProviderMeasures.markMeasureEnd(
				COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_INIT_DOC,
			);

			const commonAttributes = {
				collabService: 'ncs',
				isLivePage,
			};
			const onSuccess = () => {
				experienceTracker.succeed({
					name: currentEditorPageLoadExperienceName,
					attributes: {
						...commonAttributes,
					},
				});
			};
			const onFailure = (error: Error) => {
				if (
					error instanceof WaitForPublishButtonComponentUnmountedError ||
					error instanceof WaitForPublishButtonPreviousCallNotCompletedError ||
					error instanceof WaitForPublishButtonTabInactiveError
				) {
					experienceTracker.abort({
						name: currentEditorPageLoadExperienceName,
						reason: error.message,
						attributes: {
							...commonAttributes,
						},
					});
				} else {
					experienceTracker.fail({
						name: currentEditorPageLoadExperienceName,
						attributes: {
							...commonAttributes,
							failureReason: error.message,
						},
						error,
					});
				}
			};

			const isEmbedded = isEmbeddedConfluence_DO_NOT_USE();
			if (isLivePage || isEmbedded) {
				onSuccess();
			} else {
				if (
					!expValEquals('cc_delay_succeeding_edit_page_until_publish_button', 'isEnabled', true)
				) {
					// When experiment is disabled, succeed edit-page/load immediately.
					onSuccess();
				}
				waitForPublishButtonToBeEnabled().then(
					() => {
						createAnalyticsEvent({
							type: 'sendOperationalEvent',
							data: {
								action: 'enabled',
								actionSubject: 'editPagePublishButton',
								source: 'useInit',
								objectId: contentId,
							},
						}).fire();

						if (
							expValEquals('cc_delay_succeeding_edit_page_until_publish_button', 'isEnabled', true)
						) {
							// When experiment is enabled, succeed edit-page/load after publish button appears.
							onSuccess();
						}
					},
					(error: Error) => {
						const wouldBeAnAbort =
							error instanceof WaitForPublishButtonComponentUnmountedError ||
							error instanceof WaitForPublishButtonPreviousCallNotCompletedError ||
							error instanceof WaitForPublishButtonTabInactiveError;
						createAnalyticsEvent({
							type: 'sendOperationalEvent',
							data: {
								action: 'failedToEnable',
								actionSubject: 'editPagePublishButton',
								source: 'useInit',
								objectId: contentId,
								attributes: {
									wouldBeAnAbort,
									errorMessage: error.message,
								},
							},
						}).fire();

						if (
							expValEquals('cc_delay_succeeding_edit_page_until_publish_button', 'isEnabled', true)
						) {
							// When experiment is enabled, fail edit-page/load after timeout.
							onFailure(error);
						}
					},
				);
			}
		};
		collabEditProvider.on('init', handleInit);
		return () => {
			collabEditProvider.off('init', handleInit);

			const experienceState = experienceTracker.getExperienceState(
				currentEditorPageLoadExperienceName,
			);
			const experienceHasStopped = experienceState?.hasStopped ?? true;
			const experienceMatchesContentId = experienceState?.attributes?.contentId === contentId;

			// If we leave the editor page and the page we transition to immediately calls `useInit` (e.g. going from edit/live page to live page), this logic may undesirably try to abort the newly started experience. Using `experienceMatchesContentId` prevents this.
			if (!experienceHasStopped && experienceMatchesContentId) {
				experienceTracker.abort({
					name: currentEditorPageLoadExperienceName,
					reason: 'Unmounted before experience finished',
					attributes: {
						isLivePage,
					},
				});
			}
		};
	}, [
		waitForPublishButtonToBeEnabled,
		collabEditProvider,
		experienceTracker,
		nativeCollabActions,
		currentEditorPageLoadExperienceName,
		isLivePage,
		contentId,
		createAnalyticsEvent,
	]);

	return {
		collabEditInitialized,
		titleFromInit: title,
	};
};
